import React, { useState, useEffect } from 'react';
import { 
  Sparkles, 
  CheckCircle, 
  ExternalLink, 
  CornerDownRight, 
  Calendar, 
  Clock, 
  Share2, 
  ArrowLeft, 
  BookOpen, 
  ShieldCheck, 
  Award,
  ChevronRight,
  Copy,
  Check,
  FileText
} from 'lucide-react';
import { db, doc, getDoc, collection, getDocs } from '../firebase';
import { MemberRoomArticle, generateArticleSchedule } from '../data/memberroom_seed';

interface ArticlePageViewProps {
  slug: string;
  onNavigateHome: () => void;
  onOpenChatAndShop?: () => void;
}

export const ArticlePageView: React.FC<ArticlePageViewProps> = ({ 
  slug, 
  onNavigateHome,
  onOpenChatAndShop
}) => {
  const [article, setArticle] = useState<MemberRoomArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [relatedArticles, setRelatedArticles] = useState<MemberRoomArticle[]>([]);

  const cleanSlug = decodeURIComponent(slug).replace(/^\/|\/$/g, '').replace(/^article\//, '');

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      try {
        // 1. Try finding in LocalStorage first
        const localSaved = localStorage.getItem('giffarine_memberroom_articles');
        let allArticles: MemberRoomArticle[] = [];
        
        if (localSaved) {
          allArticles = JSON.parse(localSaved);
        } else {
          allArticles = generateArticleSchedule();
        }

        // Try to match slug
        let found = allArticles.find(a => 
          decodeURIComponent(a.slug).replace(/^\/|\/$/g, '') === cleanSlug ||
          a.id === cleanSlug
        );

        // 2. Try Firestore if not found
        if (!found) {
          const snap = await getDocs(collection(db, 'memberroom'));
          if (!snap.empty) {
            const list: MemberRoomArticle[] = [];
            snap.forEach(d => list.push(d.data() as MemberRoomArticle));
            allArticles = list;
            found = list.find(a => 
              decodeURIComponent(a.slug).replace(/^\/|\/$/g, '') === cleanSlug ||
              a.id === cleanSlug
            );
          }
        }

        if (found) {
          setArticle(found);
          // Set browser tab title
          document.title = `${found.title} | PinkGiff กิฟฟารีนออนไลน์`;
          
          // Find 3 other related articles
          const related = allArticles
            .filter(a => a.id !== found?.id && a.published)
            .slice(0, 3);
          setRelatedArticles(related.length > 0 ? related : allArticles.filter(a => a.id !== found?.id).slice(0, 3));
        } else {
          // Fallback to day-1 article-1
          const fallback = allArticles[0] || generateArticleSchedule()[0];
          setArticle(fallback);
          document.title = `${fallback.title} | PinkGiff`;
        }
      } catch (err) {
        console.error('Error loading article:', err);
        const fallback = generateArticleSchedule()[0];
        setArticle(fallback);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [cleanSlug]);

  const handleCopyLink = () => {
    const fullUrl = window.location.href;
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  // Markdown renderer for rich article body
  const renderMarkdownContent = (content: string) => {
    if (!content) return null;
    const lines = content.split('\n');
    let inTable = false;
    let tableHeaders: string[] = [];
    let tableRows: string[][] = [];

    const elements = lines.map((line, idx) => {
      const trimmed = line.trim();

      // Skip title block if duplicated in markdown
      if (idx === 0 && trimmed.startsWith('# ')) return null;
      if (trimmed.startsWith('URL Slug:') || trimmed.startsWith('Keyword หลัก:')) return null;

      // Table formatting
      if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
        const cells = trimmed.split('|').map(c => c.trim()).filter(c => c.length > 0);
        if (cells.some(c => c.includes('---'))) return null;

        if (!inTable) {
          inTable = true;
          tableHeaders = cells;
          return null;
        } else {
          tableRows.push(cells);
          return null;
        }
      } else if (inTable) {
        inTable = false;
        const finishedHeaders = [...tableHeaders];
        const finishedRows = [...tableRows];
        tableHeaders = [];
        tableRows = [];

        if (finishedHeaders.length > 0) {
          return (
            <div key={`table-${idx}`} className="my-8 overflow-x-auto rounded-2xl border border-pink-200 shadow-sm bg-white">
              <table className="w-full text-left text-xs sm:text-sm border-collapse">
                <thead>
                  <tr className="bg-gradient-to-r from-pink-100/80 via-rose-50 to-pink-50 text-slate-800 font-extrabold border-b border-pink-200">
                    {finishedHeaders.map((h, i) => (
                      <th key={i} className="px-4 py-3.5 sm:px-6">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-pink-100 text-slate-700">
                  {finishedRows.map((row, rIdx) => (
                    <tr key={rIdx} className="hover:bg-pink-50/30 transition-colors">
                      {row.map((cell, cIdx) => (
                        <td key={cIdx} className="px-4 py-3.5 sm:px-6">{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
      }

      if (inTable) return null;

      // Headers
      if (trimmed.startsWith('# ')) {
        return (
          <h1 key={idx} className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 mt-8 mb-4 border-b-2 border-pink-100 pb-3 tracking-tight leading-snug">
            {trimmed.replace('# ', '')}
          </h1>
        );
      }
      if (trimmed.startsWith('## ')) {
        return (
          <h2 key={idx} className="text-xl sm:text-2xl md:text-3xl font-extrabold text-slate-900 mt-10 mb-4 flex items-center gap-2.5 text-[#BE185D] tracking-tight">
            <Sparkles className="w-6 h-6 text-[#E60067] shrink-0" />
            <span>{trimmed.replace('## ', '')}</span>
          </h2>
        );
      }
      if (trimmed.startsWith('### ')) {
        return (
          <h3 key={idx} className="text-lg sm:text-xl font-bold text-slate-800 mt-6 mb-3">
            {trimmed.replace('### ', '')}
          </h3>
        );
      }

      // Bullet Points
      if (trimmed.startsWith('* ')) {
        return (
          <div key={idx} className="flex items-start gap-3 my-3 text-sm sm:text-base text-slate-700 pl-2">
            <span className="w-2 h-2 rounded-full bg-[#E60067] mt-2.5 shrink-0" />
            <span className="flex-1 leading-relaxed">{trimmed.replace('* ', '')}</span>
          </div>
        );
      }
      if (trimmed.startsWith('- ')) {
        return (
          <div key={idx} className="flex items-start gap-3 my-2.5 text-sm sm:text-base text-slate-700 pl-4">
            <CornerDownRight className="w-4 h-4 text-pink-500 mt-1 shrink-0" />
            <span className="flex-1 leading-relaxed">{trimmed.replace('- ', '')}</span>
          </div>
        );
      }

      // Image Placement
      if (trimmed.startsWith('[รูปภาพประกอบ:') || trimmed.startsWith('[รูปภาพ:')) {
        const altText = trimmed.match(/Alt:\s*([^\]]+)/)?.[1] || "สมัครกิฟฟารีนออนไลน์ PinkGiff";
        const titleText = trimmed.match(/รูปภาพ(?:ประกอบ)?:\s*([^\](]+)/)?.[1] || "ภาพประกอบบทความกิฟฟารีน";
        return (
          <figure key={idx} className="my-8 p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-pink-50 via-rose-50/40 to-white border border-pink-200/80 flex flex-col items-center justify-center text-center shadow-md">
            <div className="w-14 h-14 rounded-2xl bg-pink-100 flex items-center justify-center text-pink-600 mb-3 shadow-inner">
              <FileText className="w-7 h-7" />
            </div>
            <figcaption className="text-sm sm:text-base font-bold text-slate-800 mb-1">{titleText.trim()}</figcaption>
            <span className="text-xs bg-pink-100 text-[#E60067] px-3 py-1 rounded-full font-mono mt-1 font-semibold">
              Alt: {altText}
            </span>
          </figure>
        );
      }

      // Styled Giffarine Registration Action Button
      if (trimmed.startsWith('🔘 **[สมัครสมาชิกกิฟฟารีนออนไลน์กับทีม PinkGiff]**') || trimmed.includes('[สมัครสมาชิกกิฟฟารีนออนไลน์กับทีม PinkGiff]')) {
        return (
          <div key={idx} className="my-10 p-6 sm:p-8 rounded-3xl bg-gradient-to-r from-pink-500 via-[#E60067] to-rose-600 text-white shadow-xl shadow-pink-500/25 text-center flex flex-col items-center justify-center relative overflow-hidden group">
            <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
            <span className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-md px-3.5 py-1 rounded-full text-xs font-bold mb-3 uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5 text-amber-300" /> สิทธิพิเศษทีม PinkGiff รหัส 132000061
            </span>
            <h3 className="text-xl sm:text-2xl font-black mb-2 tracking-tight">
              สมัครสมาชิกกิฟฟารีนออนไลน์ 180 บาทตลอดชีพ
            </h3>
            <p className="text-pink-100 text-xs sm:text-sm max-w-lg mb-6 leading-relaxed">
              รับส่วนลดทันที 25% ตลอดชีวิต พร้อมเข้าถึงระบบเครื่องมือช่วยทำการตลาดออนไลน์และคอร์สเทรนนิ่งฟรี 100%
            </p>
            <a 
              href="https://www.giffarine.com/member/register/132000061"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 px-8 py-4 bg-white text-[#E60067] hover:bg-pink-50 font-black text-base sm:text-lg rounded-2xl shadow-lg transition-all duration-300 transform hover:scale-105 active:scale-95 text-center"
            >
              <CheckCircle className="w-6 h-6 text-emerald-500 shrink-0" />
              <span>กดลงทะเบียนสมัครออนไลน์ทันที</span>
              <ExternalLink className="w-5 h-5 opacity-70 shrink-0" />
            </a>
          </div>
        );
      }

      // Sub-links/Action steps inside content
      if (trimmed.startsWith('👉 [') && trimmed.endsWith(')')) {
        const textMatch = trimmed.match(/\[([^\]]+)\]/);
        const textLabel = textMatch ? textMatch[1] : "สมัครสมาชิกออนไลน์";
        return (
          <div key={idx} className="my-4 text-center">
            <a 
              href="https://www.giffarine.com/member/register/132000061"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm sm:text-base text-[#E60067] hover:text-pink-700 font-extrabold underline decoration-pink-400 decoration-2 underline-offset-4 transition-all"
            >
              <span>{textLabel}</span>
              <ExternalLink className="w-4 h-4 shrink-0" />
            </a>
          </div>
        );
      }

      // Blockquotes / Warnings
      if (trimmed.startsWith('> ')) {
        return (
          <blockquote key={idx} className="my-6 p-5 sm:p-6 rounded-2xl border-l-4 border-[#E60067] bg-pink-50/60 text-sm sm:text-base text-slate-800 leading-relaxed font-medium">
            {trimmed.replace('> ', '')}
          </blockquote>
        );
      }

      // Separator
      if (trimmed === '---') {
        return <hr key={idx} className="my-8 border-pink-200/60" />;
      }

      // Empty Lines
      if (trimmed === '') return <div key={idx} className="h-3" />;

      // Normal Paragraphs
      return <p key={idx} className="text-sm sm:text-base text-slate-700 leading-relaxed my-4">{line}</p>;
    });

    return <div className="space-y-1">{elements}</div>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center space-y-3">
          <div className="w-12 h-12 border-4 border-[#E60067] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm font-bold text-slate-600">กำลังโหลดบทความคุณภาพสูง...</p>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-slate-50 py-16 px-4 text-center">
        <h2 className="text-2xl font-bold text-slate-800 mb-2">ไม่พบบทความที่ต้องการ</h2>
        <p className="text-slate-500 mb-6 text-sm">บทความอาจยังไม่ได้รับการเผยแพร่หรือเปลี่ยนเส้นทาง</p>
        <button
          onClick={onNavigateHome}
          className="px-6 py-2.5 bg-[#E60067] text-white font-bold rounded-xl"
        >
          กลับหน้าหลัก
        </button>
      </div>
    );
  }

  const publicUrl = window.location.href;

  return (
    <div className="min-h-screen bg-slate-50 font-['Prompt',sans-serif] text-slate-800 pb-20">
      {/* Top Breadcrumb & Actions Bar */}
      <div className="bg-white border-b border-pink-100 shadow-2xs sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 py-3 sm:px-6 flex items-center justify-between gap-4">
          <button 
            onClick={onNavigateHome}
            className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-[#BE185D] hover:text-[#E60067] transition-colors cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" /> 
            <span>หน้าหลัก PinkGiff</span>
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={handleCopyLink}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-pink-50 hover:bg-pink-100 text-[#E60067] text-xs font-bold rounded-xl border border-pink-200 transition-all cursor-pointer"
              title="คัดลอกลิงก์บทความ"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
              <span>{copied ? 'คัดลอกแล้ว!' : 'คัดลอกลิงก์'}</span>
            </button>
            <a
              href="https://www.giffarine.com/member/register/132000061"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-1.5 bg-gradient-to-r from-[#E60067] to-pink-500 hover:from-pink-600 hover:to-rose-500 text-white text-xs font-extrabold rounded-xl shadow-xs transition-all"
            >
              <span>สมัครสมาชิกทันที</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>

      {/* Main Article Container */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 pt-8 pb-12">
        {/* Article Header Card */}
        <header className="bg-white rounded-3xl p-6 sm:p-10 border border-pink-100 shadow-sm mb-8">
          {/* Breadcrumb Tags */}
          <div className="flex flex-wrap items-center gap-2 mb-4 text-xs">
            <span className="bg-pink-100 text-[#E60067] px-3 py-1 rounded-full font-bold flex items-center gap-1">
              <BookOpen className="w-3.5 h-3.5" /> แคมเปญ 60 วัน (วันที่ {article.dayNumber})
            </span>
            <span className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full font-semibold">
              คีย์เวิร์ด: {article.targetKeyword}
            </span>
            <span className="text-slate-400 flex items-center gap-1 ml-auto">
              <Clock className="w-3.5 h-3.5" /> อ่าน 5 นาที
            </span>
          </div>

          {/* H1 Main Heading */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 leading-tight mb-4">
            {article.title}
          </h1>

          {/* Meta Description Lead */}
          <p className="text-sm sm:text-base text-slate-600 leading-relaxed bg-pink-50/50 p-4 sm:p-5 rounded-2xl border border-pink-100/80 mb-6 font-medium">
            {article.description}
          </p>

          {/* Author Badge & Live URL display */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-4 border-t border-pink-100/60 text-xs text-slate-500">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-[#E60067] to-pink-400 text-white flex items-center justify-center font-black text-sm shadow-xs">
                PG
              </div>
              <div>
                <p className="font-bold text-slate-800 text-sm flex items-center gap-1">
                  ทีมงาน PinkGiff Online
                  <ShieldCheck className="w-4 h-4 text-emerald-500" />
                </p>
                <p className="text-slate-500">รหัสผู้แนะนำอย่างเป็นทางการ 132000061</p>
              </div>
            </div>

            {/* Permanent Public Slug Box */}
            <div className="flex items-center gap-2 bg-slate-50 px-3.5 py-2 rounded-xl border border-slate-200 text-slate-600 font-mono text-[11px] overflow-hidden">
              <span className="text-pink-600 font-bold">URL:</span>
              <span className="truncate max-w-[220px]">/{article.slug}/</span>
              <button 
                onClick={handleCopyLink}
                className="text-slate-400 hover:text-pink-600 transition-colors ml-1"
                title="คัดลอกลิงก์"
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </header>

        {/* Article Body Content */}
        <article className="bg-white rounded-3xl p-6 sm:p-10 md:p-12 border border-pink-100 shadow-sm leading-relaxed text-slate-800">
          {renderMarkdownContent(article.content)}
        </article>

        {/* Author Contact & Team PinkGiff Consultation Footer */}
        <section className="mt-10 bg-gradient-to-br from-pink-50 via-white to-rose-50 rounded-3xl p-6 sm:p-8 border border-pink-200/80 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 text-center md:text-left">
            <h3 className="text-lg sm:text-xl font-extrabold text-slate-900 flex items-center justify-center md:justify-start gap-2">
              <Award className="w-5 h-5 text-[#E60067]" />
              <span>ต้องการปรึกษาการทำกิฟฟารีนออนไลน์กับทีม PinkGiff?</span>
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 max-w-lg leading-relaxed">
              เรามีระบบ AI Chat & Shop และทีมพี่เลี้ยงให้คำแนะนำฟรีแบบ 1-on-1 ไม่บังคับยอด ไม่ต้องประชุมใหญ่ สมัครเพียง 180 บาทตลอดชีพ
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
            <a
              href="https://www.giffarine.com/member/register/132000061"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-gradient-to-r from-[#E60067] to-pink-500 hover:from-pink-600 hover:to-rose-500 text-white font-extrabold text-sm rounded-xl shadow-md transition-all flex items-center gap-1.5"
            >
              <span>สมัครสมาชิกออนไลน์</span>
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </section>

        {/* Related 60-Day Articles */}
        {relatedArticles.length > 0 && (
          <section className="mt-12">
            <h3 className="text-lg sm:text-xl font-bold text-slate-800 mb-5 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-[#E60067]" />
              <span>บทความแนะนำอื่นๆ ในแคมเปญ 60 วัน</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {relatedArticles.map((rel) => (
                <div 
                  key={rel.id}
                  onClick={() => {
                    window.history.pushState({}, '', `/${rel.slug}`);
                    window.dispatchEvent(new Event('popstate'));
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="bg-white rounded-2xl p-5 border border-pink-100 shadow-xs hover:shadow-md hover:border-pink-300 transition-all cursor-pointer flex flex-col justify-between group"
                >
                  <div>
                    <span className="text-[11px] font-bold text-[#E60067] bg-pink-50 px-2.5 py-0.5 rounded-full mb-2 inline-block">
                      วันที่ {rel.dayNumber} (บทความที่ {rel.articleNumber})
                    </span>
                    <h4 className="font-bold text-slate-800 text-sm group-hover:text-[#E60067] transition-colors line-clamp-2 mb-2">
                      {rel.title}
                    </h4>
                    <p className="text-slate-500 text-xs line-clamp-2">
                      {rel.description}
                    </p>
                  </div>
                  <div className="mt-4 pt-3 border-t border-pink-50 flex items-center justify-between text-xs font-semibold text-[#E60067]">
                    <span>อ่านบทความนี้</span>
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};
